import {
  BreakPointCondition,
  Button,
  Icon,
  Notice,
  Text,
  useBreakpointIf,
  HStack,
  VStack,
} from '@genial/components';
import { Color, Spacing, Typography } from '@genial/design-tokens';
import { Utils, useBreakpointValue } from '@genial/utils';
import { useNavigation } from '@react-navigation/native';
import { Box } from 'native-base';
import React, { useEffect, useMemo } from 'react';
import { useRecoilValue } from 'recoil';

import { SignedInNavigationScreenNavigationProp } from '../../../../../routes/types';
import { historicalSearchFiltersState } from '../../../../../store/transactionFlow/atom';

import { getNoticeText } from './constants';

export type FilterModalTypes = {
  finalDate: Date | null;
  initialDate: Date | null;
  productType: number | null;
};

interface FilterProps {
  loading: boolean;
  onFilterChange: (filters: FilterModalTypes) => void;
  onDownload: () => void;
}
export const Filters: React.FC<FilterProps> = ({ onFilterChange, onDownload, loading }) => {
  const isMobile = useBreakpointIf(BreakPointCondition.EqualOrDown, 'sm');
  const { navigate } = useNavigation<SignedInNavigationScreenNavigationProp>();
  const [selectedDate, setSelectedDate] = React.useState<Date | undefined>({
    initialDate: undefined,
    finalDate: undefined,
  });

  const { appliedFilters } = useRecoilValue(historicalSearchFiltersState);

  const filterToApply = useMemo(() => {
    if (appliedFilters.length > 0) {
      return appliedFilters.reduce(
        (acc, filter) => {
          if (filter.fieldName === 'periodType') {
            if (
              Array.isArray(filter.filter) &&
              filter.filter.length > 0 &&
              filter.filter[0] !== 'outros'
            ) {
              const today = new Date();
              acc.initialDate = new Date(
                new Date().setDate(new Date().getDate() - Number(filter.filter[0]))
              );
              acc.finalDate = today;
            }
          }

          if (filter.fieldName === 'positionDate') {
            if (
              filter.filter &&
              typeof filter.filter === 'object' &&
              'initialDate' in filter.filter
            ) {
              acc.initialDate = (filter.filter as { initialDate?: Date | null }).initialDate;
            }

            if (
              filter.filter &&
              typeof filter.filter === 'object' &&
              'finalDate' in filter.filter
            ) {
              acc.finalDate = (filter.filter as { finalDate?: Date | null }).finalDate;
            }
          }

          if (filter.fieldName === 'productType') {
            if (filter.filter && typeof filter.filter === 'string') {
              acc.productType = Number(filter.filter);
            }
          }

          return acc;
        },
        { initialDate: undefined, finalDate: undefined, productType: undefined } as FilterModalTypes
      );
    }

    return null;
  }, [appliedFilters]);

  useEffect(() => {
    if (filterToApply) {
      setSelectedDate((prev) => {
        return {
          ...prev,
          initialDate: filterToApply.initialDate || prev.initialDate,
          finalDate: filterToApply.finalDate || prev.finalDate,
        };
      });
      onFilterChange(filterToApply);
    }
  }, [filterToApply]);

  const Filter: React.FC = () => {
    return useBreakpointValue({
      base: (
        <VStack
          justifyContent="space-around"
          alignItems="flex-start"
          space={Spacing['inline.xxxs']}>
          <Button
            isDisabled={loading}
            _text={{ textTransform: 'none' }}
            onPress={() => navigate('HistoricalSearchFiltersModal')}
            paddingX={Spacing['inline.xxxs']}
            variant="secondary"
            {...(!appliedFilters.length &&
              !isMobile && { rightIcon: <Icon name="Funnel" size="xxxs" /> })}>
            <HStack alignItems="center" justifyContent="center" space={Spacing['inline.nano']}>
              <Text variant="semibold" fontSize={Typography['fontsize.md']}>
                Filtrar
              </Text>
              {!!appliedFilters.length && (
                <Box
                  rounded="full"
                  w="20px"
                  h="20px"
                  alignItems="center"
                  justifyContent="center"
                  bg={Color['brand.tertiary.light']}>
                  <Text
                    variant="regular"
                    fontSize={Typography['fontsize.xs']}
                    color="neutral.white">
                    {appliedFilters.length}
                  </Text>
                </Box>
              )}
            </HStack>
          </Button>
          {selectedDate.initialDate && (
            <Notice
              type="warning"
              backgroundColor={Utils.ColorToken.WithOpacity(
                'feedback.attention.lightest',
                'opacity.medium'
              )}
              textObjects={[{ text: getNoticeText(selectedDate), variant: 'regular' }]}
            />
          )}
        </VStack>
      ),
      sm: (
        <HStack justifyContent="space-around" alignItems="center" space={Spacing['inline.nano']}>
          <Button
            isDisabled={loading}
            _text={{ textTransform: 'none' }}
            onPress={() => navigate('HistoricalSearchFiltersModal')}
            paddingX={Spacing['inline.xxxs']}
            variant="secondary"
            {...(!appliedFilters.length &&
              !isMobile && { rightIcon: <Icon name="Funnel" size="xxxs" /> })}>
            <HStack alignItems="center" justifyContent="center" space={Spacing['inline.nano']}>
              <Text variant="semibold" fontSize={Typography['fontsize.md']}>
                Filtrar
              </Text>
              {!!appliedFilters.length && (
                <Box
                  rounded="full"
                  w="20px"
                  h="20px"
                  alignItems="center"
                  justifyContent="center"
                  bg={Color['brand.tertiary.light']}>
                  <Text
                    variant="regular"
                    fontSize={Typography['fontsize.xs']}
                    color="neutral.white">
                    {appliedFilters.length}
                  </Text>
                </Box>
              )}
            </HStack>
          </Button>
          {selectedDate.initialDate && (
            <Notice
              type="warning"
              backgroundColor={Utils.ColorToken.WithOpacity(
                'feedback.attention.lightest',
                'opacity.medium'
              )}
              textObjects={[{ text: getNoticeText(selectedDate), variant: 'regular' }]}
            />
          )}
        </HStack>
      ),
    });
  };

  return (
    <HStack justifyContent="space-between" alignItems="center">
      <Filter />
      <Button
        variant="textLink"
        p={0}
        justifyContent="start"
        onPress={onDownload}
        isDisabled={loading}
        rightIcon={<Icon name="Download" size="xxxs" color={Color['brand.tertiary.principal']} />}>
        BAIXAR
      </Button>
    </HStack>
  );
};
